import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";
import { addDays, startOfDay, endOfDay, startOfMonth, endOfMonth, subMonths, subDays } from "date-fns";
import { getFormattedDateTime } from "@/utils";
import { Search, Eye, MoreHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PaginationControls } from "@/components/common/PaginationControl";
import { Link } from "react-router-dom";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { OrderApi } from "@/api/order.api";
import { OrderTableSkeleton } from "@/components/transactions/OrderTableSkeleton";
import { OrdersFilters } from "@/components/common/OrdersFilters";
import { APIClient } from "@/api/axiosClient";
import { downloadTransactionsCSV, downloadTransactionsPDF } from "@/utils/pdfGenerator";

interface Order {
  id: number;
  createdDate: number | string;
  programMemberId: string;
  programVO?: { programName?: string };
  lineItems?: any[];
  orderAmount: number;
  currencyVO?: { iso3CurrencyCode?: string };
  orderStatus: string;
}

interface OrderResponse {
  orderVOS: Order[];
  totalRecords: number;
}

interface Column {
  header: string;
  accessorKey?: keyof Order;
  id?: string;
  cell?: (row: Order) => React.ReactNode;
}

const RewardPointOrders = () => {
  const { refreshToken } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [filters, setFilters] = useState<any>({});
  const [searchType, setSearchType] = useState("orderId");
  const [searchTerm, setSearchTerm] = useState("");
  const [redemptionMerchants, setRedemptionMerchants] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [dateRange, setDateRange] = useState("none");

  const handleApiError = async (response: Response) => {
    if (response.status === 401 || response.status === 403) {
      const refreshSuccess = await refreshToken();
      if (refreshSuccess) {
        return true;
      }
      throw new Error('Authentication failed');
    }
    return false;
  };

  // Helper to get start and end in millis for a given range
  const getDateRangeMillis = (range) => {
    const now = new Date();
    let start, end;
    switch (range) {
      case "30days":
        start = startOfDay(addDays(now, -29));
        end = endOfDay(now);
        break;
      case "7days":
        start = startOfDay(addDays(now, -6));
        end = endOfDay(now);
        break;
      case "today":
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case "yesterday":
        start = startOfDay(addDays(now, -1));
        end = endOfDay(addDays(now, -1));
        break;
      case "thisMonth":
        start = startOfMonth(now);
        end = endOfDay(now);
        break;
      case "lastMonth":
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      default:
        start = null;
        end = null;
    }
    return {
      startDateInMillis: start ? start.getTime() : null,
      endDateInMillis: end ? end.getTime() : null
    };
  };

  const fetchOrders = async (overrideFilters = filters, overrideSearchType = searchType, overrideSearchTerm = searchTerm) => {
    try {
      setLoading(true);
      setError(null);
      let orderDate = null;
      const dateMillis = getDateRangeMillis(dateRange);
      if (dateRange && dateRange !== 'none') {
        const start = dateMillis.startDateInMillis ? new Date(dateMillis.startDateInMillis) : new Date();
        orderDate = start.toISOString().split('T')[0];
      }
      const payload: any = {
        pageNumber: currentPage,
        pageSize,
        sortBy,
        sortOrder,
        orderStatus: overrideFilters.status || null,
        orderDate,
      };
      if (overrideSearchType === 'orderId' && overrideSearchTerm) {
        payload.id = overrideSearchTerm;
      } else if (overrideSearchType === 'memberId' && overrideSearchTerm) {
        payload.programMemberId = overrideSearchTerm;
      }
      if (overrideFilters.programVO) payload.programVO = overrideFilters.programVO;
      if (overrideFilters.redemptionMerchantId) payload.redemptionMerchantId = overrideFilters.redemptionMerchantId;
      const { data } = await OrderApi.fetchOrders(payload);
      setOrders(data.orderVOS);
      setTotalOrders(data.totalRecords);
    } catch (error) {
      setError(error?.message || 'Failed to fetch orders');
      toast.error(error?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(filters, searchType, searchTerm);
  }, [currentPage, pageSize, sortBy, sortOrder, dateRange, searchType, searchTerm, filters]);

  useEffect(() => {
    async function fetchRedemptionMerchants() {
      try {
        const res = await fetch('https://core-engine.dev.loyyal.net/redemptionMerchant/getRedemptionMerchants', {
          method: 'POST',
          headers: {
            'accept': 'application/json, text/plain, */*',
            'content-type': 'application/json',
            // Add authentication headers if needed
          },
          body: JSON.stringify({
            merchantName: null,
            merchantCode: null,
            warehouseCountryId: null,
            pageNumber: 0,
            pageSize: 100,
            sortOrder: "desc",
            sortBy: "createdDate"
          }),
          credentials: 'include', // if you need to send cookies
        });
        const data = await res.json();
        setRedemptionMerchants(data.redemptionMerchantVOS || []);
      } catch (e) {
        setRedemptionMerchants([]);
      }
    }
    fetchRedemptionMerchants();
  }, []);

  useEffect(() => {
    async function fetchPrograms() {
      try {
        const response = await APIClient(
          '/program/getAllPrograms',
          {},
          {
            requestType: 'get',
            baseUrl: import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL,
          }
        );
        // Map to only the fields needed for the filter (programName, programCode)
        const programList = Array.isArray(response.data)
          ? response.data.map((p) => ({ programName: p.programName, programCode: p.programCode, id: p.id }))
          : [];
        setPrograms(programList);
      } catch (err) {
        setPrograms([]);
      }
    }
    fetchPrograms();
  }, []);

  const getStatusBadge = (status: string) => {
    const label = status
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/(^|\s)\S/g, (l) => l.toUpperCase());
    let color = 'bg-gray-100 text-gray-800';
    if (status === 'FINALIZED') color = 'bg-green-100 text-green-800';
    else if (status === 'CREATED') color = 'bg-blue-100 text-blue-800';
    else if (status === 'POINTS_REDEMPTION_SUCCESS') color = 'bg-yellow-100 text-yellow-800';
    // return <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${color}`}>{label}</span>;
    return <Badge variant="outline" className={`text-xs font-medium ${color}`}>{label}</Badge>
  };

  const columns: Column[] = [
    {
      header: "Order ID",
      accessorKey: "id",
      cell: (row) => <div>{row.id}</div>,
    },
    {
      header: "Created Date",
      accessorKey: "createdDate",
      cell: (row) => <div>{row.createdDate ? getFormattedDateTime(Number(row.createdDate)) || '-' : '-'}</div>,
    },
    {
      header: "Member ID",
      accessorKey: "programMemberId",
      cell: (row) => <div>{row.programMemberId || '-'}</div>,
    },
    {
      header: "Program",
      cell: (row) => <div>{row.programVO?.programName || '-'}</div>,
    },
    {
      header: "Merchant",
      cell: (row) => <div>{row.lineItems?.[0]?.giftCardProductVO?.redemptionMerchantVO?.merchantName || '-'}</div>,
    },
    {
      header: "Order Amount",
      accessorKey: "orderAmount",
      cell: (row) => <div>{row.orderAmount}</div>,
    },
    {
      header: "Currency",
      cell: (row) => <div>{row.currencyVO?.iso3CurrencyCode || '-'}</div>,
    },
    {
      header: "Order Status",
      accessorKey: "orderStatus",
      cell: (row) => getStatusBadge(row.orderStatus),
    },
    {
      header: "Actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/rewardpoint/orders/${row.id}`}>View Details</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(0);
    fetchOrders(newFilters, searchType, searchTerm);
  };

  const handleExport = async (formatType: "csv" | "pdf") => {
    try {
      let orderDate = null;
      const dateMillis = getDateRangeMillis(dateRange);
      if (dateRange && dateRange !== 'none') {
        const start = dateMillis.startDateInMillis ? new Date(dateMillis.startDateInMillis) : new Date();
        orderDate = start.toISOString().split('T')[0];
      }
      // Fetch all records with current filters/search
      const payload: any = {
        pageNumber: 0,
        pageSize: 10000, // or a suitably large number
        sortBy,
        sortOrder,
        orderStatus: filters.status || null,
        orderDate,
      };
      if (searchType === 'orderId' && searchTerm) {
        payload.id = searchTerm;
      } else if (searchType === 'memberId' && searchTerm) {
        payload.programMemberId = searchTerm;
      }
      if (filters.programVO) payload.programVO = filters.programVO;
      if (filters.redemptionMerchantId) payload.redemptionMerchantId = filters.redemptionMerchantId;

      const { data } = await OrderApi.fetchOrders(payload);
      const allOrders = data.orderVOS || [];

      // Use columns definition to build export data matching the UI
      const exportData = allOrders.map((order: any) => {
        const row: Record<string, any> = {};
        columns.forEach((col) => {
          let value = '';
          if (col.cell) {
            const cellContent = col.cell(order);
            if (typeof cellContent === 'string' || typeof cellContent === 'number') {
              value = String(cellContent);
            } else if (React.isValidElement(cellContent)) {
              value = (cellContent.props && cellContent.props.children)
                ? (Array.isArray(cellContent.props.children)
                  ? cellContent.props.children.join(' ')
                  : cellContent.props.children)
                : '';
            }
          } else if (col.accessorKey) {
            value = order[col.accessorKey] ?? '';
          }
          if (typeof value === 'string') {
            value = value.replace(/<[^>]+>/g, '');
          }
          row[typeof col.header === 'string' ? col.header : ''] = value;
        });
        return row;
      });

      if (formatType === "csv") {
        import("@/utils/pdfGenerator").then(({ downloadCSV, generateOrdersCSV }) => {
          downloadCSV(exportData, generateOrdersCSV, "orders");
        });
        toast.success("Orders exported as CSV successfully!");
      } else if (formatType === "pdf") {
        import("@/utils/pdfGenerator").then(({ downloadOrdersPDF }) => {
          downloadOrdersPDF(exportData);
        });
        toast.success("Orders exported as PDF successfully!");
      }
    } catch (error) {
      console.error('Error exporting orders:', error);
      toast.error(`Failed to export orders as ${formatType.toUpperCase()}`);
    }
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    setCurrentPage(0);
  };

  const handleReset = () => {
    setSearchTerm("");
    setFilters({});
    setCurrentPage(0);
    setDateRange("none");
    fetchOrders();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
          <p className="text-gray-500">View and manage your orders</p>
        </div>
      </div>

      {/* Filters */}
      <OrdersFilters
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        onDateRangeChange={handleDateRangeChange}
        onReset={handleReset}
        programs={programs}
        merchants={redemptionMerchants}
        searchType={searchType}
        setSearchType={setSearchType}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        dateRange={dateRange}
      />

      {/* Table */}
      <DataTable
        title="All Orders"
        data={orders}
        columns={columns}
        customTableBody={loading ? <OrderTableSkeleton /> : undefined}
      />

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Rows per page:</span>
          <Select value={pageSize.toString()} onValueChange={(value) => { setPageSize(Number(value)); setCurrentPage(0); }}>
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PaginationControls
          totalPages={Math.ceil(totalOrders / pageSize)}
          currentPage={currentPage}
          onNextHandler={() => setCurrentPage(p => Math.min(Math.ceil(totalOrders / pageSize) - 1, p + 1))}
          onPrevHandler={() => setCurrentPage(p => Math.max(0, p - 1))}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
};

export default RewardPointOrders; 